/**
 * PipelineProgress Component
 *
 * Version History:
 * v4.40 - 2026-11-01 - BATCH-2026-10-31B R12: a failed run's startFailure.reason (the stable code the server sends) is
 *                      shown as its sentence from the onboarding language file (startFailure.<code>, fallback for an
 *                      unknown code), through the i18n hook the setup screens use
 * v4.26 - 2026-10-06 - INDUSTRY-CODE-NOT-STORED (S8; F11): a Retry answered INDUSTRY_CODE_MISSING also calls the
 *                      optional onNeedIndustry, so the page can show the industry screen
 * v4.21 - 2026-10-01 - CONNECTOR-HEALTH-STATE-H2 (H2-6d, F82): Retry Pipeline inspects the setup/complete answer;
 *                      not_ready shows the Finish Setup message (shared helper) and does not resume polling
 * v3.11 - 2026-02-14 - Replaced MUI Stepper with canvas particle animation (PipelineAnimation)
 * v2.88 - 2026-02-06 - Added 5th approval step, awaiting_approval detection, onNavigateToReview prop
 * v2.54 - 2026-02-03 - Use hourglass icons for pending/running states (animated for running)
 * v2.52 - 2026-02-03 - Added manual refresh button to progress view; updated time estimate text
 * v2.51 - 2026-02-03 - Refresh Status instead of Retry; treat NotFound as "still starting"
 * v2.50 - 2026-02-03 - Captive error state with retry, no Continue to Dashboard
 * v2.44 - 2026-02-02 - Multi-phase pipeline UI (sync → discover → transform → gold)
 * v2.6 - 2026-01-23 - BFF pattern: removed tenantId prop, resolved server-side
 * v2.2 - 2026-01-23 - Initial implementation
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import { setupService, connectionsService, SetupStatusResponse } from '../services/api';
import { finishSetupDecision, FinishSetupDecision, HealthCarrier } from '../utils/connectionHealthDisplay'; // v4.21 - H2
// v3.11 - Canvas particle animation replaces MUI Stepper
import PipelineAnimation from './PipelineAnimation';
import { useTranslation } from 'react-i18next'; // v4.40 - R12
import { START_FAILURE_NAMESPACE, startFailureText } from '../utils/startFailureText'; // v4.40 - R12

// v2.88 - Added onNavigateToReview prop for proposal approval flow
interface PipelineProgressProps {
  onComplete: () => void;
  onNavigateToReview?: () => void;
  onNeedIndustry?: () => void; // v4.26 - INDUSTRY-CODE-NOT-STORED S8
}

// v2.44 - BFF Pattern: tenantId resolved server-side from SWA auth
// v2.51 - Refresh status instead of retry; treat NotFound as transient
// v2.88 - Added awaiting_approval detection
const PipelineProgress: React.FC<PipelineProgressProps> = ({ onComplete, onNavigateToReview, onNeedIndustry }) => {
  const { t, i18n } = useTranslation(START_FAILURE_NAMESPACE); // v4.40 - R12: the setup screens' language file
  const [status, setStatus] = useState<SetupStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // v3.95 - UX-KPI-08: retry state for pipeline error recovery
  const [isRetrying, setIsRetrying] = useState(false);
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6d): the not-ready answer to a Retry, shown until the next action
  const [notReady, setNotReady] = useState<FinishSetupDecision | null>(null);

  // v2.51 - Helper to check if error is transient (pipeline still starting up)
  const isTransientError = (errorMsg: string): boolean => {
    const transientPatterns = [
      'NotFound',
      'not found',
      'still starting',
      'initializing',
      'starting up',
      '404',
    ];
    return transientPatterns.some(pattern =>
      errorMsg.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  // Poll for status updates
  // v2.51 - Treat NotFound as transient, keep polling
  const pollStatus = useCallback(async () => {
    try {
      const response = await setupService.getStatus();
      console.log('v2.51: Pipeline status response:', response);
      setStatus(response);
      setError(null); // Clear any previous error on successful poll

      // v2.88 - Check for awaiting_approval → navigate to review
      if (response.pipelineStatus === 'awaiting_approval' || response.workflowState === 'awaiting_approval') {
        console.log('v2.88: Pipeline awaiting approval, navigating to review');
        if (onNavigateToReview) {
          onNavigateToReview();
        }
        return;
      }

      // Check for completion
      if (response.pipelineStatus === 'completed' || response.setupComplete) {
        setIsComplete(true);
      } else if (response.pipelineStatus === 'failed' && response.startFailure?.reason) {
        // v4.40 - R12: the server's stable code; the sentence comes from the language file
        setError(startFailureText(t, (key: string) => i18n.exists(key, { ns: START_FAILURE_NAMESPACE }),
                                  response.startFailure.reason));
      } else if (response.pipelineStatus === 'failed') {
        // Find the failed phase error
        const phases = response.phases;
        let errorFound = false;
        if (phases) {
          for (const [phaseName, phase] of Object.entries(phases)) {
            if (phase.status === 'failed' && phase.error) {
              // v2.47 - Extract message from nested error object
              // v2.48 - Fixed TypeScript error handling - 2026-02-03
              let errorMsg: string;
              if (typeof phase.error === 'object') {
                errorMsg = phase.error.message || JSON.stringify(phase.error);
              } else {
                errorMsg = phase.error;
              }

              // v2.51 - Check if this is a transient error (pipeline still starting)
              if (isTransientError(errorMsg)) {
                console.log(`v2.51: Transient error in ${phaseName}, continuing to poll:`, errorMsg);
                // Don't set error, keep polling
                return;
              }

              console.error(`v2.51: Phase ${phaseName} failed:`, phase.error);
              setError(`${phaseName} failed: ${errorMsg}`);
              errorFound = true;
              break;
            }
          }
        }
        if (!errorFound) {
          setError('Pipeline failed. Please try again.');
        }
      }
    } catch (err: any) {
      // v2.51 - Check if the fetch error itself is transient
      const errMsg = err?.message || String(err);
      if (isTransientError(errMsg)) {
        console.log('v2.51: Transient fetch error, continuing to poll:', errMsg);
        // Don't set error, keep polling
        return;
      }
      console.error('v2.51: Failed to poll pipeline status:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Poll every 5 seconds
    const interval = setInterval(pollStatus, 5000);
    pollStatus(); // Initial call

    return () => clearInterval(interval);
  }, [pollStatus]);

  useEffect(() => {
    if (isComplete) {
      // Wait a moment then redirect
      const timer = setTimeout(() => {
        onComplete();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, onComplete]);

  // v2.51 - Handle refresh status - just re-poll, don't restart pipeline
  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    setError(null);
    setNotReady(null);
    try {
      await pollStatus();
    } finally {
      setIsRefreshing(false);
    }
  };

  // v3.95 - UX-KPI-08: Retry pipeline from scratch after failure
  const handleRetryPipeline = async () => {
    setIsRetrying(true);
    setError(null); // optimistic clear — show animation immediately on click
    setNotReady(null);
    try {
      const result: any = await setupService.complete();
      // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6d, F82): a not-ready answer is not a restart — show the same
      // message Finish Setup shows (shared helper over the pending connections' verdicts) and do not resume polling.
      if (result && result.status === 'not_ready') {
        let rows: HealthCarrier[] = [];
        const tenantId = sessionStorage.getItem('userTenantId');
        if (tenantId && (result.connectionsPending || []).length > 0) {
          try {
            rows = ((await connectionsService.list(tenantId)).connections || []) as HealthCarrier[];
          } catch (e) {
            // the pending connections render as "Status unknown"
          }
        }
        setNotReady(finishSetupDecision(result, rows, 0));
        // v4.26 - INDUSTRY-CODE-NOT-STORED S8: no stored industry code -> the page shows the industry screen
        if (result.reason === 'INDUSTRY_CODE_MISSING') {
          onNeedIndustry?.();
        }
        return;
      }
      await pollStatus(); // resume polling after restart
    } catch (err: any) {
      setError(err?.message || 'Failed to restart pipeline. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  // v2.51 - Render error state - captive with refresh status only
  if (error || notReady) {
    // Extract readable error message
    let errorMessage: string = notReady ? notReady.message : (error as string);
    if (!notReady && typeof error === 'object') {
      errorMessage = (error as any).message || JSON.stringify(error);
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Pipeline Error
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
          {errorMessage}
        </Typography>

        {/* v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6b/d): each pending connection with its verdict */}
        {notReady && notReady.pending.length > 0 && (
          <Box component="ul" sx={{ mb: 3, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
            {notReady.pending.map(p => (
              <li key={p.id}>
                <Typography variant="body2" component="span">
                  <strong>{p.label}</strong>: {p.healthLabel}{p.healthReason ? ` — ${p.healthReason}` : ''}
                </Typography>
              </li>
            ))}
          </Box>
        )}

        <Alert severity="info" sx={{ mb: 3, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
          An error occurred during processing. You can retry the pipeline from the beginning.
        </Alert>

        {/* v3.95 - UX-KPI-08: Primary retry button — calls POST /api/setup/complete */}
        <Button
          variant="contained"
          onClick={handleRetryPipeline}
          disabled={isRetrying}
          sx={{ minWidth: 150, mr: 1 }}
        >
          {isRetrying ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
          {isRetrying ? 'Restarting...' : 'Retry Pipeline'}
        </Button>

        {/* Keep Refresh Status as secondary option */}
        <Button
          variant="outlined"
          onClick={handleRefreshStatus}
          disabled={isRefreshing}
          startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          sx={{ minWidth: 150 }}
        >
          {isRefreshing ? 'Checking...' : 'Refresh Status'}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          If retrying does not help, please contact support at support@mylojic.com
        </Typography>
      </Box>
    );
  }

  // Render completion state
  if (isComplete) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
          All Done!
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Your dashboard is ready. Redirecting...
        </Typography>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const progress = status?.overallProgress || 0;

  // v3.11 - Canvas particle animation driven by polling data
  return (
    <Box sx={{ py: 2 }}>
      <PipelineAnimation
        currentPhase={status?.currentPhase || null}
        phases={status?.phases ? Object.fromEntries(
          Object.entries(status.phases).map(([k, v]) => [k, { status: v.status }])
        ) : null}
        overallProgress={progress}
        pipelineStatus={status?.pipelineStatus || null}
        phaseDescription={status?.phaseDescription}
      />

      {/* v2.52 - Time estimate + manual refresh below animation */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This may take several minutes depending on data volume. Feel free to grab a coffee!
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleRefreshStatus}
          disabled={isRefreshing}
          startIcon={isRefreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
        >
          {isRefreshing ? 'Checking...' : 'Refresh Status'}
        </Button>
      </Box>
    </Box>
  );
};

export default PipelineProgress;
